import { useState } from 'react';

import { c } from 'ttag';

import { userSettingsThunk } from '@proton/account/userSettings';
import { useUserSettings } from '@proton/account/userSettings/hooks';
import { useApi } from '@proton/app-context/useApi';
import { useNotifications } from '@proton/app-context/useNotifications';
import { Button } from '@proton/atoms/Button/Button';
import { Href } from '@proton/atoms/Href/Href';
import { useDispatch } from '@proton/redux-shared-store/sharedProvider';
import { CacheType } from '@proton/redux-utilities/interface';
import { disableHighSecurity, enableHighSecurity } from '@proton/shared/lib/api/settings';
import { TelemetrySecurityCenterEvents } from '@proton/shared/lib/api/telemetry';
import { PROTON_SENTINEL_NAME, SETTINGS_PROTON_SENTINEL_STATE } from '@proton/shared/lib/constants';
import { traceInitiativeError } from '@proton/shared/lib/helpers/sentry';
import { getKnowledgeBaseUrl } from '@proton/shared/lib/helpers/url';
import { isProtonSentinelEligible } from '@proton/shared/lib/helpers/userSettings';
import ProtonSentinelLogo from '@proton/styles/assets/img/illustrations/proton-sentinel-shield.svg';

import { getDisabledString, getEnabledString } from '../../../../../containers/credentialLeak/helpers';
import getBoldFormattedText from '../../../../../helpers/getBoldFormattedText';
import useSettingsLink from '../../../../link/useSettingsLink';
import { useModalStateObject } from '../../../../modalTwo/useModalState';
import Toggle from '../../../../toggle/Toggle';
import DrawerAppSection from '../../shared/DrawerAppSection';
import { sendSecurityCenterReport } from '../securityCenterTelemetry';
import ProtonSentinelUpsellModal from './modal/ProtonSentinelUpsellModal';

const ProtonSentinel = () => {
    const api = useApi();
    const { createNotification } = useNotifications();
    const dispatch = useDispatch();
    const [userSettings, loadingUserSettings] = useUserSettings();
    const upsellModal = useModalStateObject();
    const [loadingSentinel, setLoadingSentinel] = useState(false);
    const goToSettings = useSettingsLink();
    const isSentinelEligible = isProtonSentinelEligible(userSettings);
    const isProtonSentinelEnabled = userSettings.HighSecurity.Value === SETTINGS_PROTON_SENTINEL_STATE.ENABLED;

    if (loadingUserSettings) {
        return null;
    }

    const handleToggleSentinel = async (newHighSecurityState: Boolean) => {
        if (!isSentinelEligible) {
            upsellModal.openModal(true);
            return;
        }

        try {
            setLoadingSentinel(true);
            if (newHighSecurityState) {
                await api(enableHighSecurity());
                void sendSecurityCenterReport(api, {
                    event: TelemetrySecurityCenterEvents.proton_sentinel_toggle,
                    dimensions: {
                        sentinel_toggle_value: 'enabled',
                    },
                });
                createNotification({ text: getEnabledString(PROTON_SENTINEL_NAME) });
            } else {
                await api(disableHighSecurity());
                void sendSecurityCenterReport(api, {
                    event: TelemetrySecurityCenterEvents.proton_sentinel_toggle,
                    dimensions: {
                        sentinel_toggle_value: 'disabled',
                    },
                });
                createNotification({ text: getDisabledString(PROTON_SENTINEL_NAME) });
            }
            await dispatch(userSettingsThunk({ cache: CacheType.None }));
        } catch (e) {
            console.error(e);
            traceInitiativeError('drawer-security-center', e);
        } finally {
            setLoadingSentinel(false);
        }
    };

    const learnMoreLink = (
        <Href key="proton-sentinel-link" href={getKnowledgeBaseUrl('/proton-sentinel')} className="inline-block">{c(
            'Link'
        ).t`Learn more`}</Href>
    );

    return (
        <>
            <DrawerAppSection>
                <div className="flex flex-nowrap items-center gap-2 mt-2">
                    <div className="shrink-0 flex">
                        <img src={ProtonSentinelLogo} alt="" />
                    </div>
                    <h3 className="flex-1 text-rg">
                        <label htmlFor="proton-sentinel-toggle">{PROTON_SENTINEL_NAME}</label>
                    </h3>
                    <Toggle
                        id="proton-sentinel-toggle"
                        loading={loadingSentinel}
                        checked={isProtonSentinelEnabled}
                        onChange={({ target }) => {
                            void handleToggleSentinel(target.checked);
                        }}
                        className="shrink-0"
                    />
                </div>
                {isProtonSentinelEnabled ? (
                    <>
                        <p className="mt-1 mb-2 text-sm color-weak">
                            {
                                // translator: **active** will be put in bold, please keep syntax to make it work properly
                                getBoldFormattedText(
                                    c('Security Center - Info').t`Sentinel is **active** and safeguarding your account.`
                                )
                            }
                        </p>
                        <Button
                            color="weak"
                            fullWidth
                            onClick={() => {
                                goToSettings(`/security#logs`, undefined, false);
                            }}
                            className="mb-2"
                        >
                            {c('Security Center - Action').t`View events`}
                        </Button>
                    </>
                ) : (
                    <p className="mt-1 mb-2 text-sm color-weak">
                        {c('Security Center - Info')
                            .jt`Our cutting-edge AI-driven security solution designed for users seeking heightened protection for their accounts. ${learnMoreLink}`}
                    </p>
                )}
            </DrawerAppSection>
            {upsellModal.render && <ProtonSentinelUpsellModal modalProps={upsellModal.modalProps} />}
        </>
    );
};

export default ProtonSentinel;
